import { useCallback, useEffect, useRef } from 'react'
import usePresenceChannel, {
  PresencePayload,
  PresenceViewer as Viewer,
} from 'app/frontend/base/hooks/usePresenceChannel'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'

type Options = {
  visualId?: number
  onPlanUpdated?: (plan: VisualPlan, sourceUserId?: number) => void
  onPlanDeleted?: (planId: number) => void
}

type VisualPlanChannelPayload = PresencePayload & {
  viewer?: Viewer
  visual_plan?: VisualPlan
  plan_id?: number
  source_user_id?: number
}

const useVisualPlanChannel = ({
  visualId,
  onPlanUpdated,
  onPlanDeleted,
}: Options) => {
  const onPlanUpdatedRef = useRef(onPlanUpdated)
  const onPlanDeletedRef = useRef(onPlanDeleted)

  useEffect(() => {
    onPlanUpdatedRef.current = onPlanUpdated
  }, [onPlanUpdated])

  useEffect(() => {
    onPlanDeletedRef.current = onPlanDeleted
  }, [onPlanDeleted])

  const handlePayload = useCallback((payload: VisualPlanChannelPayload) => {
    if (payload.type === 'plan_updated') {
      if (!payload.visual_plan) return
      onPlanUpdatedRef.current?.(payload.visual_plan, payload.source_user_id)
    }

    if (payload.type === 'plan_deleted') {
      if (payload.plan_id === undefined) return
      onPlanDeletedRef.current?.(payload.plan_id)
    }
  }, [])

  const { viewers, requiresReload } =
    usePresenceChannel<VisualPlanChannelPayload>({
      channelName: 'VisualPlanChannel',
      channelId: visualId,
      channelIdParam: 'plan_id',
      onPayload: handlePayload,
    })

  return {
    viewers,
    requiresReload,
  }
}

export default useVisualPlanChannel
